import {
  Box,
  Paper,
  Typography,
  TextField,
  MenuItem,
  Button,
  Autocomplete,
  Chip,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import GroupsIcon from '@mui/icons-material/Groups'; 
import CloseIcon from '@mui/icons-material/Close';
import { updateField } from "../../../../features/editCompany/editCompanySlice";

const partnersMock = ["Michael Brown", "Sarah Lee", "Alex Johnson"];

export default function OwnershipStep() {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const draft = useSelector(state => state.editCompany.draft);

  const fieldStyle = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      backgroundColor: '#fff',
    },
    '& .MuiInputBase-input': { 
      paddingTop: '10px',
      paddingBottom: '10px'
    },
    '& legend': { display: 'none' },
  };

  const labelStyle = {
    display: 'block',
    mb: 0.5,
    color: '#888',
    fontWeight: 500,
    fontSize: '0.85rem'
  };

  return (
    // Ensure the background is light grey and has padding at the bottom for the fixed bar
    <Box sx={{ minHeight: "100vh", pb: 10,  position: "relative" }}>
      <Paper
        elevation={0}
        sx={{
          maxWidth: 800,
          mx: "auto",
          mt: 4,
          p: 4,
          borderRadius: '16px',
          border: '1px solid #e0e0e0',
          bgcolor: "#FAFAFA"
        }}
      >
        <Box display="flex" alignItems="center" gap={1} mb={4}>
          <GroupsIcon sx={{ fontSize: 18, color: '#888' }} />
          <Typography variant="subtitle2" fontWeight={700} color="#333">
            Internal Ownership & Classification
          </Typography>
        </Box>

        <Box display="flex" flexDirection="column" gap={3}>
          <Box>
            <Typography sx={labelStyle}>Partners</Typography>
            <Autocomplete
              multiple
              options={partnersMock}
              value={draft.partners || ["Michael Brown"]}
              onChange={(_, value) => dispatch(updateField({ field: "partners", value}))}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option}
                    {...getTagProps({ index })}
                    size="small"
                    deleteIcon={<CloseIcon style={{ fontSize: '14px' }} />}
                    sx={{ borderRadius: '4px', bgcolor: '#f5f5f5' }}
                  />
                ))
              }
              renderInput={(params) => <TextField {...params} size="small" sx={fieldStyle} />}
            />
          </Box>

          <Box>
            <Typography sx={labelStyle}>Board Representative</Typography>
            <TextField
              select
              fullWidth
              size="small"
              value={draft.boardRepresentative || "D/C"}
              onChange={(e) => dispatch(updateField({ field: "boardRepresentative", value: e.target.value}))}
              sx={fieldStyle}
            >
              <MenuItem value="D/C">D/C</MenuItem>
              <MenuItem value="Observer">Observer</MenuItem>
            </TextField>
          </Box>

          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
            <Box>
              <Typography sx={labelStyle}>Fund Family</Typography>
              <TextField
                select
                fullWidth
                size="small"
                value={draft.fundFamily || "VI"}
                sx={fieldStyle}
              >
                <MenuItem value="VI">VI</MenuItem>
                <MenuItem value="VII">VII</MenuItem>
              </TextField>
            </Box>
            <Box>
              <Typography sx={labelStyle}>Vintage Year</Typography>
              <TextField
                select
                fullWidth
                size="small"
                value={draft.vintageYear || 2021}
                sx={fieldStyle}
              >
                {[2024, 2023, 2022, 2021, 2020].map((year) => (
                  <MenuItem key={year} value={year}>{year}</MenuItem>
                ))}
              </TextField>
            </Box>
          </Box>

          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
            <Box>
              <Typography sx={labelStyle}>Grade</Typography>
              <TextField select fullWidth size="small" value={draft.grade || "A"} sx={fieldStyle}>
                <MenuItem value="A">A</MenuItem>
                <MenuItem value="B">B</MenuItem>
              </TextField>
            </Box>
            <Box>
              <Typography sx={labelStyle}>Rank</Typography>
              <TextField select fullWidth size="small" value={draft.rank || 1} sx={fieldStyle}>
                {[1, 2, 3, 4, 5].map((rank) => (
                  <MenuItem key={rank} value={rank}>{rank}</MenuItem>
                ))}
              </TextField>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* 🔷 FIXED FOOTER - The Z-index ensures it stays on top of everything */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          bgcolor: '#fff',
          borderTop: '1px solid #e0e0e0',
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 6,
          zIndex: 9999, // Added very high z-index
          boxShadow: '0 -2px 10px rgba(0,0,0,0.05)'
        }}
      >
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon sx={{ fontSize: '18px !important' }} />}
          onClick={() => navigate(`/company/${companyId}/edit/details`)}
          sx={{
            color: '#444',
            borderColor: '#e0e0e0',
            textTransform: 'none',
            borderRadius: '25px',
            px: 3,
            fontWeight: 600,
            fontSize: '0.85rem',
            '&:hover': { borderColor: '#ccc', bgcolor: 'transparent', color:"black" }
          }}
        >
          Back
        </Button>

        <Button
          variant="contained"
          endIcon={<ArrowForwardIcon sx={{ fontSize: '18px !important' }} />}
          onClick={() => navigate(`/company/${companyId}/edit/investment`)}
          sx={{
            bgcolor: '#005682',
            textTransform: 'none',
            borderRadius: '25px',
            px: 4,
            fontWeight: 600,
            fontSize: '0.85rem',
            '&:hover': { bgcolor: '#004466' }
          }}
        >
          Next
        </Button>
      </Box>
    </Box>
  );
}